import { toast } from 'react-hot-toast';
import logger from '../../utils/logger';
import { probeMedia, deviceSnapshot, mediaErrorMessage } from '../../utils/mediaReadiness';

// OD-3: temsilci müsaitken cihaz parmak izi bu aralıkla karşılaştırılır (getUserMedia değil, ucuz).
export const MEDIA_MONITOR_INTERVAL_MS = 15000;
const MEDIA_RECHECK_DEBOUNCE_MS = 500;

let mediaCheckInFlight = null;
let mediaMonitor = null;

// Görüşme sürerken (ya da peerConnection hâlâ canlıyken) izleme ve 'technical_issue' politikası çalışmaz (OD-3).
export function isAgentInCall(state) {
    return Boolean(state.peerConnection) || ['connecting', 'connected', 'ice-failed'].includes(state.callStatus);
}


// Video kalitesi ayarlarını döndüren yardımcı fonksiyon
function getVideoConstraintsByQuality(quality, devicePerformance = 'medium') {
    const constraints = {
        low: {
            width: { ideal: 320 },
            height: { ideal: 240 },
            frameRate: { max: 15 }
        },
        medium: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            frameRate: { ideal: 24 }
        },
        high: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
        },
        ultra: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 }
        }
    };

    // Cihaz performansına göre kaliteyi ayarla
    if (quality === 'auto') {
        if (devicePerformance === 'low') {
            return constraints.low;
        } else if (devicePerformance === 'medium') {
            return constraints.medium;
        } else {
            return constraints.high;
        }
    }

    return constraints[quality] || constraints.medium;
}

const createMediaSlice = (set, get) => ({
    // WebRTC ve Medya durumu
    localStream: null,
    remoteStream: null,
    peerConnection: null,
    isMicrophoneEnabled: true,
    isCameraEnabled: true,
    videoQuality: 'high', // 'low', 'medium', 'high', 'ultra' veya 'auto'
    networkQuality: 'good', // 'poor', 'average', 'good'
    devicePerformance: 'high', // 'low', 'medium', 'high'
    availableCameras: [], // Kullanılabilir kamera listesi
    selectedCameraId: null, // Seçilen kamera ID'si
    isFrontCamera: true, // Ön kamera mı arka kamera mı

    // Temsilci kamera/mikrofon hazırlığı (videocall ortak kaydı §B.5). Sunucu mediaReady=false iken
    // temsilciye müşteri atamaz; varsayılan false, ilk başarılı kontrolde true olur.
    mediaReady: false,
    mediaErrorCode: null,
    mediaChecked: false,
    mediaCheckedAt: 0,

    // Gerçek getUserMedia denemesi; eşzamanlı çağrılar tek denemeyi paylaşır.
    checkMediaReadiness: () => {
        if (mediaCheckInFlight) return mediaCheckInFlight;
        mediaCheckInFlight = probeMedia(typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined)
            .then((result) => {
                get().setMediaReadiness(result.ready, result.errorCode);
                if (!result.ready) get().applyMediaFailurePolicy(result.errorCode);
                return result;
            })
            .finally(() => { mediaCheckInFlight = null; });
        return mediaCheckInFlight;
    },

    setMediaReadiness: (ready, errorCode) => {
        set({ mediaReady: ready, mediaErrorCode: ready ? null : (errorCode || 'UNKNOWN'), mediaChecked: true, mediaCheckedAt: Date.now() });
        get().reportMediaStatus();
    },

    // Kontrol nereden tetiklenirse tetiklensin (kayıt onayı, sunucu reddi, "Tekrar dene", izleme):
    // müsait görünen ama kamerası düşmüş temsilci 'technical_issue'a alınır — durum ile hazırlık ayrışmasın.
    applyMediaFailurePolicy: (errorCode) => {
        const state = get();
        if (state.userType !== 'agent' || isAgentInCall(state)) return;
        if (state.currentStatus?.status_type !== 'available') return;
        const agentId = state.user?.id;
        if (!agentId) return;
        toast.error(mediaErrorMessage(errorCode));
        state.setTechnicalIssue(agentId, 'Kamera/mikrofon hazır değil', { force: true });
    },

    // agent-media-status: kayıt onayı (agent-registered) ve her kontrol sonucundan sonra gönderilir.
    reportMediaStatus: () => {
        const { socket, userType, mediaReady, mediaErrorCode, mediaChecked } = get();
        if (!socket || !socket.connected || userType !== 'agent' || !mediaChecked) return;
        socket.emit('agent-media-status', mediaReady ? { ready: true } : { ready: false, errorCode: mediaErrorCode });
    },

    // OD-3: yalnız temsilci müsaitken çalışır. Cihaz takılıp çıkarılması, izin değişikliği ya da
    // periyodik cihaz listesi farkı tam kontrolü tetikler; düşerse applyMediaFailurePolicy devreye girer.
    startMediaMonitoring: () => {
        get().stopMediaMonitoring();
        const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
        if (!mediaDevices) return;

        let stopped = false;
        let snapshot = null;
        let debounceTimer = null;

        const recheck = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(async () => {
                if (stopped) return;
                await get().checkMediaReadiness();
                snapshot = await deviceSnapshot(mediaDevices);
            }, MEDIA_RECHECK_DEBOUNCE_MS);
        };

        deviceSnapshot(mediaDevices).then((s) => { if (snapshot === null) snapshot = s; });

        const onDeviceChange = () => recheck();
        if (typeof mediaDevices.addEventListener === 'function') {
            mediaDevices.addEventListener('devicechange', onDeviceChange);
        }

        const permissionStatuses = [];
        if (typeof navigator !== 'undefined' && navigator.permissions && typeof navigator.permissions.query === 'function') {
            ['camera', 'microphone'].forEach((name) => {
                // Firefox 'camera'/'microphone' adlarını desteklemeyebilir — reddi yok sayılır.
                navigator.permissions.query({ name }).then((status) => {
                    if (stopped || !status || typeof status.addEventListener !== 'function') return;
                    status.addEventListener('change', recheck);
                    permissionStatuses.push(status);
                }).catch(() => { });
            });
        }

        const interval = setInterval(async () => {
            const current = await deviceSnapshot(mediaDevices);
            if (stopped || snapshot === null) return;
            if (current !== snapshot) {
                snapshot = current;
                recheck();
            }
        }, MEDIA_MONITOR_INTERVAL_MS);

        mediaMonitor = {
            stop: () => {
                stopped = true;
                clearTimeout(debounceTimer);
                clearInterval(interval);
                if (typeof mediaDevices.removeEventListener === 'function') {
                    mediaDevices.removeEventListener('devicechange', onDeviceChange);
                }
                permissionStatuses.forEach((status) => status.removeEventListener('change', recheck));
            }
        };
    },

    stopMediaMonitoring: () => {
        if (mediaMonitor) {
            mediaMonitor.stop();
            mediaMonitor = null;
        }
    },

    // Mikrofonu aç/kapat
    toggleMicrophone: async () => {
        try {
            const state = get();
            let stream = state.localStream;

            // Eğer stream yoksa sadece audio iste
            if (!stream) {
                try {
                    const newStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    set({ localStream: newStream });
                    stream = newStream;
                } catch (e) {
                    return;
                }
            }

            const audioTracks = stream.getAudioTracks();

            // Eğer audio track yok ve açılmak isteniyorsa ekle
            if (audioTracks.length === 0) {
                const media = await navigator.mediaDevices.getUserMedia({ audio: true });
                const track = media.getAudioTracks()[0];
                stream.addTrack(track);

                const pc = state.peerConnection;
                if (pc) {
                    const sender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');
                    if (sender) {
                        await sender.replaceTrack(track);
                    } else {
                        pc.addTrack(track, stream);
                    }
                }

                set({ isMicrophoneEnabled: true, localStream: stream });
                return;
            }

            // Mevcut track'leri enable/disable et
            const next = !state.isMicrophoneEnabled;
            audioTracks.forEach(t => { try { t.enabled = next; } catch (_) { } });
            set({ isMicrophoneEnabled: next });
        } catch (err) {
            logger.error('toggleMicrophone error:', err);
        }
    },

    // Kamerayı aç/kapat
    toggleCamera: async () => {
        try {
            const state = get();
            let stream = state.localStream;

            // Eğer stream yoksa sadece video iste
            if (!stream) {
                try {
                    const constraints = getVideoConstraintsByQuality(state.videoQuality, state.devicePerformance);
                    const newStream = await navigator.mediaDevices.getUserMedia({ video: constraints });
                    set({ localStream: newStream });
                    stream = newStream;
                } catch (e) {
                    return;
                }
            }

            const videoTracks = stream.getVideoTracks();

            // Eğer video track yok ve açılmak isteniyorsa ekle
            if (videoTracks.length === 0) {
                const constraints = getVideoConstraintsByQuality(state.videoQuality, state.devicePerformance);
                const media = await navigator.mediaDevices.getUserMedia({ video: constraints });
                const track = media.getVideoTracks()[0];
                stream.addTrack(track);

                const pc = state.peerConnection;
                if (pc) {
                    const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
                    if (sender) {
                        await sender.replaceTrack(track);
                    } else {
                        pc.addTrack(track, stream);
                    }
                }

                set({ isCameraEnabled: true, localStream: stream });
                return;
            }

            // Mevcut track'leri enable/disable et
            const next = !state.isCameraEnabled;
            videoTracks.forEach(t => { try { t.enabled = next; } catch (_) { } });
            set({ isCameraEnabled: next });
        } catch (err) {
            logger.error('toggleCamera error:', err);
        }
    },

    // Video kalitesini ayarlama
    setVideoQuality: (quality) => {
        set({ videoQuality: quality });

        const { localStream } = get();
        if (localStream) {
            // Varolan stream video ayarlarını güncelle
            const videoTracks = localStream.getVideoTracks();
            if (videoTracks.length > 0) {
                const videoTrack = videoTracks[0];

                try {
                    const constraints = getVideoConstraintsByQuality(quality, get().devicePerformance);
                    videoTrack.applyConstraints(constraints)
                        .then(() => {
                        })
                        .catch(err => {
                            logger.error('Video kalitesi güncellenirken hata:', err);
                        });
                } catch (error) {
                    logger.error('Video kalite ayarları uygulanırken hata:', error);
                }
            }
        }
    },


});

export default createMediaSlice; 
